import { useCallback, useEffect, useState } from "react";

import { parseDetail } from "../utils/errors";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import useRequests from "../hooks/http";
import { useTenant } from "../hooks/useTenant";

interface OrgSummary {
  id: number;
  guid: string;
  name: string;
  slug: string;
}

interface PermissionWithApp extends Permission {
  appName: string;
}

function PermissionsDrawer({
  open,
  role,
  tenantId,
  onClose,
}: {
  open: boolean;
  role: Role | null;
  tenantId: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { get, post, del } = useRequests();

  const [assignedIds, setAssignedIds] = useState<number[]>([]);
  const [allPermissions, setAllPermissions] = useState<PermissionWithApp[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedPermId, setSelectedPermId] = useState<number | "">("");

  const loadData = useCallback(async () => {
    if (!role) return;
    setLoading(true);
    setError("");

    const [assignedRes, appsRes] = await Promise.all([
      get(`/admin/roles/${role.id}/permissions`),
      get(`/admin/tenants/${tenantId}/applications`),
    ]);

    if (assignedRes.status === 200 && Array.isArray(assignedRes.data)) {
      setAssignedIds(assignedRes.data);
    }

    const perms: PermissionWithApp[] = [];
    if (appsRes.status === 200 && Array.isArray(appsRes.data)) {
      const apps: Application[] = appsRes.data;
      await Promise.all(
        apps.map(async (app) => {
          const permRes = await get(`/admin/tenants/${tenantId}/applications/${app.id}/permissions`);
          if (permRes.status === 200 && Array.isArray(permRes.data)) {
            for (const p of permRes.data as Permission[]) {
              perms.push({ ...p, appName: app.name });
            }
          }
        }),
      );
    }
    setAllPermissions(perms);
    setLoading(false);
  }, [role, tenantId, get]);

  useEffect(() => {
    if (open && role) {
      loadData();
    } else {
      setAssignedIds([]);
      setAllPermissions([]);
      setSelectedPermId("");
      setError("");
    }
  }, [open, role, loadData]);

  const handleAdd = async () => {
    if (!role || selectedPermId === "") return;
    setError("");
    const { status, data } = await post(`/admin/roles/${role.id}/permissions`, null, {
      permission_id: selectedPermId,
    });
    if (status === 200 || status === 201 || status === 204) {
      setAssignedIds((prev) => [...prev, selectedPermId as number]);
      setSelectedPermId("");
    } else {
      setError(parseDetail(data?.detail, "Failed to add permission"));
    }
  };

  const handleRemove = async (permId: number) => {
    if (!role) return;
    setError("");
    const { status, data } = await del(`/admin/roles/${role.id}/permissions/${permId}`);
    if (status === 200 || status === 204) {
      setAssignedIds((prev) => prev.filter((id) => id !== permId));
    } else {
      setError(parseDetail(data?.detail, "Failed to remove permission"));
    }
  };

  const assignedPerms = allPermissions.filter((p) => assignedIds.includes(p.id));
  const unassignedPerms = allPermissions.filter((p) => !assignedIds.includes(p.id));

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 480, p: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">Permissions — {role?.name}</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("roles.permissionsDrawer.description")}
      </Typography>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Add permission</InputLabel>
          <Select
            value={selectedPermId}
            label="Add permission"
            onChange={(e) => setSelectedPermId(e.target.value as number | "")}
          >
            {unassignedPerms.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.value}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  ({p.appName})
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleAdd} disabled={selectedPermId === ""} sx={{ minWidth: 72 }}>
          Add
        </Button>
      </Box>

      {assignedPerms.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No permissions assigned to this role yet.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {assignedPerms.map((p) => (
            <Box
              key={p.id}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1.5,
                py: 1,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box>
                <Typography variant="body2" fontFamily="monospace">
                  {p.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {p.appName}
                </Typography>
              </Box>
              <IconButton size="small" color="error" onClick={() => handleRemove(p.id)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Drawer>
  );
}

export default function Roles() {
  const { t } = useTranslation();
  const { activeTenant } = useTenant();
  const { get, post, patch, del } = useRequests();

  const [roles, setRoles] = useState<Role[]>([]);
  const [roleOrgs, setRoleOrgs] = useState<Record<number, OrgSummary[]>>({});
  const [loading, setLoading] = useState(true);

  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleForm, setRoleForm] = useState({ name: "", description: "" });
  const [roleError, setRoleError] = useState("");

  const [deactivateTarget, setDeactivateTarget] = useState<Role | null>(null);

  const [permDrawerRole, setPermDrawerRole] = useState<Role | null>(null);

  useEffect(() => {
    if (!activeTenant) {
      setLoading(false);
      return;
    }
    get(`/admin/tenants/${activeTenant.id}/roles`).then(async (res) => {
      if (res.status === 200 && Array.isArray(res.data)) {
        const fetchedRoles: Role[] = res.data;
        setRoles(fetchedRoles);
        const orgMap: Record<number, OrgSummary[]> = {};
        await Promise.all(
          fetchedRoles.map(async (role) => {
            const orgRes = await get(`/admin/roles/${role.id}/organisations`);
            if (orgRes.status === 200 && Array.isArray(orgRes.data)) {
              orgMap[role.id] = orgRes.data;
            }
          }),
        );
        setRoleOrgs(orgMap);
      }
      setLoading(false);
    });
  }, [activeTenant, get]);

  const openCreate = () => {
    setEditingRole(null);
    setRoleForm({ name: "", description: "" });
    setRoleError("");
    setRoleDialogOpen(true);
  };

  const openEdit = (role: Role) => {
    setEditingRole(role);
    setRoleForm({ name: role.name, description: role.description });
    setRoleError("");
    setRoleDialogOpen(true);
  };

  const handleSaveRole = async () => {
    setRoleError("");
    if (editingRole) {
      const { data, status } = await patch(`/admin/roles/${editingRole.id}`, roleForm);
      if (status === 200) {
        setRoles((prev) => prev.map((r) => (r.id === editingRole.id ? data : r)));
        setRoleDialogOpen(false);
      } else {
        setRoleError(parseDetail(data?.detail, "Failed to update role"));
      }
    } else {
      if (!activeTenant) return;
      const { data, status } = await post(`/admin/tenants/${activeTenant.id}/roles`, null, roleForm);
      if (status === 201 || status === 200) {
        setRoles((prev) => [...prev, data]);
        setRoleDialogOpen(false);
      } else {
        setRoleError(parseDetail(data?.detail, "Failed to create role"));
      }
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    const { status } = await del(`/admin/roles/${deactivateTarget.id}`);
    if (status === 200 || status === 204) {
      setRoles((prev) => prev.map((r) => (r.id === deactivateTarget.id ? { ...r, active: false } : r)));
      setDeactivateTarget(null);
    }
  };

  if (loading) return <LinearProgress />;

  if (!activeTenant) {
    return (
      <Alert severity="info">No workspace selected. Use the workspace switcher in the sidebar to select one.</Alert>
    );
  }

  const hasAnyOrgUsage = Object.values(roleOrgs).some((orgs) => orgs.length > 0);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="h4">{t("roles.title")}</Typography>
        <Button variant="contained" onClick={openCreate}>
          {t("roles.newButton")}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("roles.description")}
      </Typography>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("roles.columns.name")}</TableCell>
              <TableCell>{t("roles.columns.description")}</TableCell>
              {hasAnyOrgUsage && <TableCell>{t("roles.columns.organisations")}</TableCell>}
              <TableCell>{t("roles.columns.status")}</TableCell>
              <TableCell>{t("roles.columns.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((role) => {
              const orgs = roleOrgs[role.id] ?? [];
              return (
              <TableRow key={role.id}>
                <TableCell>{role.name}</TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {role.description || "—"}
                  </Typography>
                </TableCell>
                {hasAnyOrgUsage && (
                  <TableCell>
                    {orgs.length === 0 ? null : (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {orgs.map((org) => (
                          <Chip
                            key={org.id}
                            size="small"
                            label={org.name}
                            component={RouterLink}
                            to={`/app/organisations/${org.id}`}
                            clickable
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    )}
                  </TableCell>
                )}
                <TableCell>
                  <Chip
                    size="small"
                    label={role.active ? t("common.active") : t("common.inactive")}
                    color={role.active ? "success" : "default"}
                  />
                </TableCell>
                <TableCell>
                  {role.active && (
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Tooltip title="Manage Permissions">
                        <IconButton size="small" onClick={() => setPermDrawerRole(role)}>
                          <LockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(role)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Button size="small" color="error" onClick={() => setDeactivateTarget(role)}>
                        Deactivate
                      </Button>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {roles.length === 0 && (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          No roles defined for this workspace yet.
        </Typography>
      )}

      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRole ? "Edit Role" : "New Role"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("roles.description")}
          </Typography>
          {roleError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {roleError}
            </Alert>
          )}
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={roleForm.name}
            onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            rows={2}
            helperText={t("roles.descriptionHelp")}
            value={roleForm.description}
            onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveRole} disabled={!roleForm.name}>
            {editingRole ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deactivateTarget)} onClose={() => setDeactivateTarget(null)}>
        <DialogTitle>Deactivate Role</DialogTitle>
        <DialogContent>
          <Typography>
            Deactivate <strong>{deactivateTarget?.name}</strong>? Users assigned this role will be unaffected but the
            role cannot be assigned to new users.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeactivate}>
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      <PermissionsDrawer
        open={Boolean(permDrawerRole)}
        role={permDrawerRole}
        tenantId={activeTenant.id}
        onClose={() => setPermDrawerRole(null)}
      />
    </Box>
  );
}
